'use client';

import React, { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import type { PageType } from '@/features/Analytics';
import { useGTM } from '@/features/Analytics';
import { TimeSlotsClient, useBooking } from '@/features/Booking';
import { MedcenterTabs } from '@/features/MedcenterTabs';
import { Badges } from '@/widgets/Badges';
import type { Badge } from '@/widgets/Badges';
import { DoctorSchedule } from '@/widgets/Schedule';
import { Link } from '@/navigation';
import { useFetchBookingData } from '@/shared/hooks/api/useFetchBookingData';
import { getDiscount } from '../utils/getDiscount';
import { getDoctorReviewsUrl } from '../utils/main';
import { Discount } from './Discount';
import { DoctorAvatar } from './DoctorAvatar';
import { DoctorCardAddress } from './DoctorCardAddress';
import { DoctorPriceInline } from './DoctorPriceInline';
import { DoctorTitle } from './DoctorTitle';
import type { IBookingDoctorData, ISchedule } from '@/entities/booking/model/types';
import type { IDoctor } from '@/entities/doctor/model/types';
import type { IMedcenter } from '@/entities/medcenter/model/types';
import type { Skill } from '@/entities/skill/model/types';
import type { DiscountInfo } from '../model/types';
interface Props {
  doctor: IDoctor;
  index: number;
  currentSkill?: string;
  isTop: boolean;
  pageType: PageType;
  setBookingStoreData: (doctorId: number, medcenterId: number, booking: IBookingDoctorData[]) => void;
}
export const DoctorCard: React.FC<Props> = ({
  doctor,
  currentSkill,
  index,
  isTop,
  pageType,
  setBookingStoreData
}) => {
  const t = useTranslations();
  const {
    trackEvent
  } = useGTM(pageType);
  const {
    setMedcenterId: setMedcenterBookingId,
    medcenterId
  } = useBooking();
  const {
    fetchBookingData
  } = useFetchBookingData({
    medcenters: doctor.medcenters,
    doctorAlias: doctor.alias
  });
  const [discount, setDiscount] = useState<DiscountInfo>(doctor.medcenters[0] ? getDiscount(doctor.medcenters[0]) : null);
  const [medcenter, setMedcenter] = useState<IMedcenter | null>(doctor.medcenters[0] || null);
  const [bookingData, setBookingData] = useState<IBookingDoctorData[]>([]);
  const [bookingMedcenterData, setBookingMedcenterData] = useState<ISchedule[] | null>(null);
  const [skill, setSkill] = useState<Skill | null>(null);
  const [badges, setBadges] = useState<Badge[]>([]);
  const getMedcenterWithDiscount = () => {
    if (!doctor?.medcenters?.length) return 0;
    const index = doctor.medcenters?.findIndex(medcenter => {
      const {
        online,
        primary
      } = medcenter.appointments;
      return online?.priceWithDiscount || primary?.priceWithDiscount;
    });
    return index !== -1 ? index : 0;
  };
  const getDoctorSkill = () => {
    if (doctor.skills.length === 0) return null;
    if (currentSkill) {
      const candidate = doctor.skills.find(skill => skill.alias === currentSkill);
      if (candidate) return candidate;
    }
    return doctor.skills[0];
  };
  const trackDoctorCardEvent = () => {
    trackEvent('click_doctor_widget', {
      label: 'doctor_avatar',
      doctorName: doctor.fullName,
      skill: currentSkill || '',
      in_ads: isTop || '',
      medcenterName: medcenter?.name || ''
    });
  };
  const preSetBookingStoreData = (doctorId: number, booking: IBookingDoctorData[]) => {
    setBookingStoreData(doctorId, medcenter!.id, booking);
  };
  const medcenterClick = (newMedcenter: IMedcenter) => {
    setMedcenter(newMedcenter);
    setMedcenterBookingId(newMedcenter.id);
  };
  useEffect(() => {
    if (!medcenter && doctor.medcenters.length > 0) {
      setMedcenter(doctor.medcenters[0]);
      setDiscount(getDiscount(doctor.medcenters[0]));
    }
  }, [doctor, medcenter]);
  useEffect(() => {
    const fetchBooking = async () => {
      const response = await fetchBookingData(medcenterId);
      if (response) {
        setBookingData(response);
      }
    };
    fetchBooking();
    setSkill(getDoctorSkill());
  }, [medcenterId]);
  useEffect(() => {
    if (medcenter && bookingData.length) {
      if (!medcenter.hasOnlineBooking) return;
      const bookingByMedcenter = bookingData.find(item => item.medcenterId === medcenter.id);
      if (bookingByMedcenter) {
        const {
          timeslotsIsEmpty,
          basedOn,
          schedule
        } = bookingByMedcenter.onlineBooking;
        if ((timeslotsIsEmpty || schedule?.length === 0) && basedOn !== 'preferred_time') return;
        setBookingMedcenterData(bookingByMedcenter.onlineBooking.schedule);
      }
    }
  }, [bookingData, medcenter]);
  useEffect(() => {
    const newBadges = [] as Badge[];
    if (isTop) {
      newBadges.push({
        type: 'top',
        label: t('doctor.profile.top')
      });
    }
    setBadges(newBadges);
  }, [isTop]);
  return <article data-doctor-id={doctor.id} className="relative flex w-full flex-col rounded-5xl bg-bgPrimary p-4 shadow-[0px_0px_30px_0px_#A6B9CB66] lg:mx-auto lg:grid lg:max-w-4xl lg:grid-cols-[179px,669px] lg:gap-x-4" data-sentry-component="DoctorCard" data-sentry-source-file="index.tsx">
      <Badges list={badges} position="right-top" data-sentry-element="Badges" data-sentry-source-file="index.tsx" />

      <div className="flex flex-col items-center">
        <Link href={{
        pathname: `/${doctor.city.alias}/doctor/${doctor.alias}`
      }} onClick={trackDoctorCardEvent} scroll={true} data-sentry-element="Link" data-sentry-source-file="index.tsx">
          <DoctorAvatar img={{
          alt: `На фотографии изображён ${getDoctorSkill()?.name ? `${getDoctorSkill()!.name}` : ''} ${doctor.fullName}`,
          src: doctor.avatar
        }} sectionId="home" isLazy={index > 5} className="w-24 lg:size-44 [&>div]:w-24 lg:[&>div]:w-44" data-sentry-element="DoctorAvatar" data-sentry-source-file="index.tsx" />
        </Link>

        <div className="mt-2 flex w-full flex-col items-center pb-2">
          <span className="mr-1 text-sm text-textPrimary md:mx-1 md:text-sm">
            {t('search.card.rating')}
            <strong> ★ {doctor.rating}</strong>
          </span>
          <Link href={getDoctorReviewsUrl(doctor)} onClick={trackDoctorCardEvent} className="text-sm text-blue-500 hover:underline md:text-xs" data-sentry-element="Link" data-sentry-source-file="index.tsx">
            {doctor.commentsCount > 0 ? `${t('search.card.countReviews', {
            count: doctor.commentsCount
          })}` : `${t('search.card.no_reviews')}`}
          </Link>
        </div>
      </div>

      <div className="flex flex-col">
        <DoctorTitle skill={skill ? skill.name : ''} name={doctor.fullName} workExperience={doctor.workExperience} nameTag="h4" skillLink={skill && skill.alias !== currentSkill ? `/${doctor.city.alias}/doctors/${skill.alias}` : ''} doctorLink={`/${doctor.city.alias}/doctor/${doctor.alias}`} isTop={isTop} medcenterName={medcenter?.name} pageType={pageType} data-sentry-element="DoctorTitle" data-sentry-source-file="index.tsx" />

        {doctor.medcenters.length > 1 ? <>
            <p className="mt-2 text-sm font-normal text-textSecondary">
              {t('doctor.profile.work_in_medcenters', {
            count: doctor.medcenters.length
          })}
            </p>

            <div className="mt-2">
              <MedcenterTabs medcenters={doctor.medcenters} medcenterClick={medcenterClick} defaultActiveIndex={getMedcenterWithDiscount()} />
            </div>
          </> : <>
            {discount && <Discount className="mt-2 w-max text-sm font-bold">
                {t(`search.card.discount.${discount.type}`, {
            discount: discount.discount
          })}
              </Discount>}
          </>}

        {medcenter && <>
            <DoctorPriceInline medcenter={medcenter} />

            <div className="mt-1">
              <DoctorCardAddress inline withGeoIcon name={medcenter.name} link={`/${doctor.city.alias}/medcenter/${medcenter.alias}`} address={medcenter.geo.address} />
            </div>

            <div className="mt-4">
              <DoctorSchedule bookingData={bookingData} medcenterId={medcenter.id} medcenters={doctor.medcenters} doctorId={doctor.id} scheduleClick={preSetBookingStoreData} />

              {bookingMedcenterData && bookingMedcenterData.length && <TimeSlotsClient initialDay={bookingMedcenterData.filter(item => !!item.timeslots && item.isWork)?.[0]} bookingData={bookingData} doctorId={doctor.id} mini pageType={pageType} timeSlotClick={preSetBookingStoreData} medcenterId={medcenter.id} />}
            </div>
          </>}
      </div>
    </article>;
};